/**
 * Auth request/response schemas — shared by the server HTTP boundary and the
 * desktop sign-in form. See AGENT.md §20.7.
 */

import { z } from 'zod';

export const signInSchema = z.object({
  email: z.string().email().transform((s) => s.trim().toLowerCase()),
  password: z.string().min(1, 'Password is required'),
  /** Optional human label for the refresh-token family (e.g. "Ada's MacBook"). */
  deviceLabel: z.string().max(120).optional(),
});
export type SignInInput = z.infer<typeof signInSchema>;

export const refreshSchema = z.object({
  refreshToken: z.string().min(1),
});
export type RefreshInput = z.infer<typeof refreshSchema>;

export const signOutSchema = z.object({
  refreshToken: z.string().min(1),
});
export type SignOutInput = z.infer<typeof signOutSchema>;

/** Shape returned by sign-in and refresh. */
export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  /** Access-token lifetime in seconds, for client-side refresh scheduling. */
  expiresIn: number;
}

export interface SessionUser {
  id: string;
  email: string;
  displayName: string | null;
}

export interface SessionTeam {
  teamId: string;
  /** The project this team belongs to (for project-scoped admin). */
  projectId: string;
  name: string;
  /** Primary role name (first role) — kept for back-compat display. */
  roleName: string | null;
  /** All role names held in this team (multi-role). */
  roleNames: string[];
  languoidCodes: string[];
  /** Effective permission atoms (roles ∪ custom) — drives the role-aware UI. */
  permissions: string[];
}

/** The user's project-level membership (project admins/observers). Null if none. */
export interface SessionProject {
  id: string;
  name: string;
  roleNames: string[];
  /** Effective project-level permission atoms (roles ∪ custom). */
  permissions: string[];
  /**
   * Languages this project member is assigned (membership `languoidCodes`). Empty = no
   * restriction (operates across all of the project's languages). A project worker (e.g.
   * a project-level judge) is scoped to these on the web.
   */
  languoidCodes: string[];
  /**
   * The project's teams — the operable surfaces for a project operator who holds
   * no direct team membership (e.g. a Project Admin running the Test Runner /
   * Export). The operator's project `permissions` apply within each.
   */
  teams: { teamId: string; name: string; languoidCodes: string[] }[];
}

/** User + team memberships; returned by `GET /auth/me` and embedded in sign-in. */
export interface SessionInfo {
  user: SessionUser;
  teams: SessionTeam[];
  /** Project-level access, if the user holds an project membership. Optional/additive. */
  project?: SessionProject | null;
}

export interface SignInResult extends AuthTokens, SessionInfo {}
